/**
 * V2WelcomePage (PRP-MCP-10 work item 4, F4): `/welcome`, where Stripe sends
 * a user after trial or subscription checkout
 * (`/welcome?plan={plan}&session_id={CHECKOUT_SESSION_ID}`). Names the plan
 * just bought and gives three numbered steps, each with a working button.
 */
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { BackendAPI } from '../../lib/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Pill } from '../components/ui/pill';

export const SUPPORT_EMAIL = 'support@anterotrail.com';

/** Plan ids that can arrive in `?plan=`, and what the page calls them. */
const PLAN_LABELS: Record<string, string> = {
  trial: '7-day trial',
  pro: 'Pro',
  pro_plus: 'Pro Plus',
};

/** The label for a plan and billing interval; the trial converts to Pro monthly. */
function boughtLabel(plan: string | null, interval: string | null): string | undefined {
  if (!plan || !PLAN_LABELS[plan]) return undefined;
  if (plan === 'trial') return '7-day trial, converts to Pro monthly';
  if (interval === 'month') return `${PLAN_LABELS[plan]}, monthly`;
  if (interval === 'annual') return `${PLAN_LABELS[plan]}, annual`;
  return PLAN_LABELS[plan];
}

/** Tier ids from GET /me/credits `plan`, for the "your plan now" line. */
const CURRENT_PLAN_LABELS: Record<string, string> = {
  pro: 'Pro',
  pro_plus: 'Pro Plus',
  importer: 'Importer',
  member: 'Team member',
  admin: 'Admin',
  free: 'Free',
};

/** Where the import control lives: the Supplement page takes the PDF or .xlsx. */
export const IMPORT_PATH = '/completeness';

/** Works with the MCP create_estimate tool: loss type, split address, then add_room dimensions. */
const CHAT_EXAMPLE =
  'Create a new estimate for a water loss at 123 Main St, Tampa, FL 33602 with a kitchen 12 by 14';

function Step({
  n,
  title,
  children,
  action,
}: {
  n: number;
  title: string;
  children: ReactNode;
  action: ReactNode;
}) {
  return (
    <li>
      <Card className="flex flex-wrap items-start gap-4 p-4" data-testid={`welcome-step-${n}`}>
        <span
          aria-hidden="true"
          className="grid h-7 w-7 flex-none place-items-center rounded-full bg-[var(--accent-soft)] text-[13px] font-[620] text-[var(--accent-ink)]"
        >
          {n}
        </span>
        <div className="min-w-0 flex-1 basis-[220px]">
          <h2 className="m-0 text-[14px] font-[600] text-[var(--tx-hi)]">{title}</h2>
          <div className="mt-1 space-y-1 text-[12.5px] leading-[1.5] text-[var(--tx-mid)]">{children}</div>
        </div>
        <div className="flex-none">{action}</div>
      </Card>
    </li>
  );
}

/** The post-checkout welcome page. */
export default function V2WelcomePage() {
  const [params] = useSearchParams();
  const bought = boughtLabel(params.get('plan'), params.get('interval'));
  const [current, setCurrent] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    Promise.resolve()
      .then(() => BackendAPI.getCredits())
      .then((c) => {
        const plan = (c as { plan?: string } | null)?.plan;
        if (live && plan) setCurrent(CURRENT_PLAN_LABELS[plan] ?? plan);
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, []);

  return (
    <div className="mx-auto w-full max-w-[760px] px-6 py-8 max-md:px-4" data-testid="welcome-page">
      <header className="mb-6">
        <div className="text-[11px] font-semibold uppercase tracking-[.06em] text-[var(--accent)]">
          You are in
        </div>
        <h1 className="mb-0 mt-2 text-[24px] font-[620] tracking-[-0.01em] text-[var(--tx-hi)]">
          {bought ? `Welcome to ${bought.split(',')[0]}` : 'Welcome to AIEA'}
        </h1>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-[12.5px] text-[var(--tx-mid)]">
          {bought && (
            <span data-testid="welcome-plan-bought">
              <Pill kind="ok">{`Just started: ${bought}`}</Pill>
            </span>
          )}
          {current && <span data-testid="welcome-plan-current">{`Your plan now: ${current}`}</span>}
        </div>
        <p className="mb-0 mt-3 text-[13.5px] text-[var(--tx-mid)]">
          Three steps from here to a finished estimate in Xactimate.
        </p>
      </header>

      <ol className="m-0 list-none space-y-3 p-0">
        <Step
          n={1}
          title="Connect your chat app"
          action={
            <Button variant="primary" asChild data-testid="welcome-connect">
              <Link to="/assistant">Connect your chat app</Link>
            </Button>
          }
        >
          <p className="m-0">Claude, ChatGPT, or Gemini, about two minutes.</p>
        </Step>
        <Step
          n={2}
          title="Import an estimate or build one in chat"
          action={
            <Button variant="primary" asChild data-testid="welcome-import">
              <Link to={IMPORT_PATH}>Import an estimate</Link>
            </Button>
          }
        >
          <p className="m-0">
            Import a PDF or Excel estimate here, or start a new one by typing in your chat app:
          </p>
          <p className="m-0 rounded-[var(--r-md)] border border-[var(--bd)] bg-[var(--bg-inset)] px-[10px] py-[7px] text-[12px] text-[var(--tx-hi)]">
            {`"${CHAT_EXAMPLE}"`}
          </p>
        </Step>
        <Step
          n={3}
          title="Export the ESX and finish in Xactimate"
          action={
            <Button variant="primary" asChild data-testid="welcome-export">
              <Link to="/">Open your estimates</Link>
            </Button>
          }
        >
          <p className="m-0">
            Open an estimate and press Download at the top of the estimate page. The first time you
            export, we ask one question about how the import went.
          </p>
        </Step>
      </ol>

      <p className="mt-6 text-[12.5px] text-[var(--tx-lo)]" data-testid="welcome-help">
        Need help? Reply to the receipt email or write to{' '}
        <a className="underline" href={`mailto:${SUPPORT_EMAIL}`}>
          {SUPPORT_EMAIL}
        </a>
        .
      </p>
    </div>
  );
}
